/**
 * Iter 57/58 UI — Hardening Chip.
 *
 * Surfaces the algorithm's own self-critique in the Match Dossier so
 * users see the confidence dampening applied and know WHY:
 *
 *  • Opponent-adjusted form (raw ppg vs adjusted ppg + strength-of-schedule Elo)
 *  • Data-quality score with per-component breakdown (h2h / form / xg / lineup / scope / odds)
 *  • Veto rules fired (data_quality_low, h2h_conflict, over_performing_form, opponent_adj_disagreement)
 *  • Original vs adjusted confidence + dampening percentage
 *  • Confirmed-lineup status (Iter 58): OK / unavailable / key player missing
 *
 * Backed by `/api/gold/hardening/:fixture_id` which runs the same
 * `predict_all_markets` pipeline the /gold engine uses, then returns
 * just the hardening-related blocks.
 */
import { useState } from "react";
import {
  Shield, ShieldAlert, ShieldCheck, TrendingUp, TrendingDown, Minus,
  Users, ChevronDown, ChevronUp, AlertOctagon, Gauge,
} from "lucide-react";

const signed = (v, digits = 2, unit = "") => {
  if (v === null || v === undefined || Number.isNaN(v)) return "—";
  const n = Number(v);
  return `${n > 0 ? "+" : ""}${n.toFixed(digits)}${unit}`;
};

const pctBar = (score, max = 1.0) => Math.max(0, Math.min(100, (Number(score) / max) * 100));

const ComponentBar = ({ label, value }) => {
  const p = pctBar(value);
  const cls = p >= 75 ? "bg-emerald-500" : p >= 50 ? "bg-amber-500" : "bg-rose-500";
  return (
    <div className="flex items-center gap-2 text-[10px]" data-testid={`harden-component-${label.toLowerCase()}`}>
      <span className="uppercase tracking-widest text-slate-400 w-14">{label}</span>
      <div className="flex-1 h-1 rounded-full bg-slate-800 overflow-hidden">
        <div className={`h-full ${cls}`} style={{ width: `${p}%` }} />
      </div>
      <span className="font-mono text-slate-300 w-8 text-right">{Math.round(p)}%</span>
    </div>
  );
};

const OutperfCard = ({ side, oaf }) => {
  if (!oaf || !oaf.sample) {
    return (
      <div className="p-2 rounded-lg border border-amber-500/10 bg-black/40" data-testid={`harden-oaf-${side.toLowerCase()}-empty`}>
        <div className="text-[10px] uppercase tracking-widest text-amber-500/60">{side}</div>
        <div className="text-[11px] text-slate-400 mt-1">No sample</div>
      </div>
    );
  }
  const outperf = Number(oaf.outperformance || 0);
  const Icon = outperf > 0.05 ? TrendingUp : outperf < -0.05 ? TrendingDown : Minus;
  const cls = outperf > 0.05 ? "text-emerald-300" : outperf < -0.05 ? "text-rose-300" : "text-slate-400";
  return (
    <div className="p-2.5 rounded-lg border border-amber-500/15 bg-black/40" data-testid={`harden-oaf-${side.toLowerCase()}`}>
      <div className="text-[10px] uppercase tracking-widest text-amber-500/60 mb-1">{side}</div>
      <div className="flex items-baseline justify-between mb-0.5">
        <span className="text-[10px] text-slate-400">Raw PPG</span>
        <span className="text-sm font-mono font-bold text-slate-200">{Number(oaf.raw_ppg || 0).toFixed(2)}</span>
      </div>
      <div className="flex items-baseline justify-between mb-1">
        <span className="text-[10px] text-slate-400">Adjusted</span>
        <span className="text-sm font-mono font-bold text-amber-200">{Number(oaf.adjusted_ppg || 0).toFixed(2)}</span>
      </div>
      <div className={`flex items-center gap-1 text-[10px] ${cls}`}>
        <Icon className="w-3 h-3" />
        {signed(outperf, 2)} vs expected
      </div>
      <div className="text-[10px] text-slate-500 mt-0.5">
        SoS Elo <span className="font-mono">{Math.round(Number(oaf.sos_elo || 1500))}</span> · {oaf.sample}m
      </div>
    </div>
  );
};

const RuleBadge = ({ rule }) => {
  const nice = {
    data_quality_low: { label: "Thin evidence", cls: "bg-amber-500/20 text-amber-300 border-amber-500/40" },
    h2h_conflict:     { label: "H2H vs form", cls: "bg-rose-500/20 text-rose-300 border-rose-500/40" },
    over_performing_form: { label: "Over-performing xG", cls: "bg-fuchsia-500/20 text-fuchsia-300 border-fuchsia-500/40" },
    opponent_adj_disagreement: { label: "Opp-adj gap", cls: "bg-cyan-500/20 text-cyan-300 border-cyan-500/40" },
  }[rule] || { label: rule, cls: "bg-slate-500/20 text-slate-300 border-slate-500/40" };
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[10px] font-semibold uppercase tracking-wider ${nice.cls}`}
      data-testid={`harden-rule-${rule}`}
    >
      <AlertOctagon className="w-3 h-3" />
      {nice.label}
    </span>
  );
};

export const HardeningChip = ({ hardening, dataQuality, veto, lineup, tactical }) => {
  const [expanded, setExpanded] = useState(false);
  const noHardening = !hardening || hardening?.error;
  if (noHardening && !dataQuality && !veto) {
    return (
      <div
        className="rounded-xl border border-amber-500/20 bg-black/50 p-3.5 text-[11px] text-amber-500/60"
        data-testid="harden-chip-empty"
      >
        <Shield className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />
        Algorithm hardening: not available for this fixture
      </div>
    );
  }

  const oafH = hardening?.opponent_adjusted_home;
  const oafA = hardening?.opponent_adjusted_away;
  const dq = dataQuality || {};
  const vt = veto || {};
  const components = dq.components || {};
  const deficient = dq.deficient || [];
  const rules = vt.rules_fired || [];
  const vetoFlag = Boolean(vt.veto_flag);
  const dampenPct = Number(vt.dampen_total_pct || 0);
  const originalConf = Number(vt.original_confidence_pct || 0);
  const adjustedConf = Number(vt.adjusted_confidence_pct || 0);
  const lineupStatus = lineup?.status || "unknown";
  const lineupQuality = Number(lineup?.lineup_quality ?? 0.5);
  const missingHome = lineup?.home_missing_key || [];
  const missingAway = lineup?.away_missing_key || [];
  const audit = hardening?.audit || [];

  const HeaderIcon = vetoFlag ? ShieldAlert : dampenPct > 0 ? Shield : ShieldCheck;
  const headerCls = vetoFlag
    ? "text-rose-400"
    : dampenPct > 15
    ? "text-amber-300"
    : "text-emerald-400";
  const headerLabel = vetoFlag
    ? "VETOED"
    : dampenPct > 0
    ? `Dampened ${dampenPct.toFixed(1)}%`
    : "All signals healthy";

  return (
    <div
      className="rounded-xl border border-amber-500/30 bg-gradient-to-br from-slate-950/60 via-black to-slate-950/40 p-3.5 space-y-3"
      data-testid="harden-chip"
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <HeaderIcon className={`w-4 h-4 ${headerCls}`} />
          <span className={`text-[11px] font-bold uppercase tracking-widest ${headerCls}`}>
            Algorithm Hardening · {headerLabel}
          </span>
        </div>
        {audit.length > 0 && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="inline-flex items-center gap-1 text-[10px] text-amber-500/70 hover:text-amber-300 transition"
            data-testid="harden-audit-toggle"
          >
            {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            audit ({audit.length})
          </button>
        )}
      </div>

      {/* Confidence adjustment banner (only when dampened) */}
      {dampenPct > 0 && (
        <div
          className="rounded-lg border border-amber-500/20 bg-amber-500/5 p-2 flex items-center gap-3"
          data-testid="harden-confidence-banner"
        >
          <Gauge className="w-4 h-4 text-amber-400 flex-shrink-0" />
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-widest text-amber-500/70">Confidence</div>
            <div className="text-sm font-mono font-bold text-slate-200">
              <span className="line-through text-slate-500">{originalConf.toFixed(1)}%</span>
              {" → "}
              <span className={vetoFlag ? "text-rose-300" : "text-amber-200"}>{adjustedConf.toFixed(1)}%</span>
            </div>
          </div>
          <div className="text-[10px] text-slate-400">{dampenPct.toFixed(1)}% off</div>
        </div>
      )}

      {/* Veto rules fired */}
      {rules.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid="harden-rules">
          {rules.map((r) => <RuleBadge key={r} rule={r} />)}
        </div>
      )}

      {/* Opponent-adjusted form side-by-side */}
      {(oafH || oafA) && (
        <div className="grid grid-cols-2 gap-2" data-testid="harden-oaf-grid">
          <OutperfCard side="Home" oaf={oafH} />
          <OutperfCard side="Away" oaf={oafA} />
        </div>
      )}

      {/* Data quality bar + component breakdown */}
      {Object.keys(components).length > 0 && (
        <div className="space-y-1.5 pt-2 border-t border-amber-500/10" data-testid="harden-data-quality">
          <div className="flex items-baseline justify-between mb-1">
            <span className="text-[10px] uppercase tracking-widest text-amber-500/70 font-semibold">Data Quality</span>
            <span className="text-[11px] font-mono text-slate-200">
              {(Number(dq.score || 0) * 100).toFixed(0)}%
              {deficient.length > 0 && (
                <span className="text-amber-500/70 ml-1.5">
                  · deficient: {deficient.join(", ")}
                </span>
              )}
            </span>
          </div>
          {Object.entries(components).map(([k, v]) => (
            <ComponentBar key={k} label={k} value={v} />
          ))}
        </div>
      )}

      {/* Lineup */}
      <div
        className="flex items-start gap-2 rounded-lg border border-amber-500/10 bg-black/40 p-2"
        data-testid="harden-lineup"
      >
        <Users className={`w-3.5 h-3.5 flex-shrink-0 mt-0.5 ${
          lineupStatus === "ok" ? "text-emerald-400" : lineupStatus === "unavailable" ? "text-slate-500" : "text-amber-400"
        }`} />
        <div className="text-[11px] leading-snug text-slate-300 flex-1">
          <span className="uppercase tracking-widest text-[10px] text-amber-500/70 mr-1">Lineup</span>
          {lineupStatus === "ok" && (
            <>
              confirmed ({(lineupQuality * 100).toFixed(0)}% quality)
              {lineup?.formation_home && lineup?.formation_away && (
                <span className="text-slate-500 ml-1.5 font-mono">
                  · {lineup.formation_home} vs {lineup.formation_away}
                </span>
              )}
              {(missingHome.length > 0 || missingAway.length > 0) && (
                <span className="block text-[10px] text-rose-300 mt-0.5">
                  key player missing: {missingHome.length ? `home (${missingHome.join(", ")})` : ""}
                  {missingHome.length && missingAway.length ? " · " : ""}
                  {missingAway.length ? `away (${missingAway.join(", ")})` : ""}
                </span>
              )}
            </>
          )}
          {lineupStatus === "unavailable" && (
            <span className="text-slate-500">not published yet · neutral prior applied</span>
          )}
          {lineupStatus === "error" && (
            <span className="text-amber-400">ingest error · neutral prior applied</span>
          )}
        </div>
      </div>

      {/* Tactical clash — Iter 59 */}
      {tactical && tactical.applied && (
        <div
          className="rounded-lg border border-cyan-500/30 bg-cyan-500/5 p-2 flex items-start gap-2"
          data-testid="harden-tactical"
        >
          <Shield className="w-3.5 h-3.5 text-cyan-400 flex-shrink-0 mt-0.5" />
          <div className="text-[11px] leading-snug text-slate-300 flex-1">
            <span className="uppercase tracking-widest text-[10px] text-cyan-500/70 mr-1">Tactical</span>
            <span className="font-mono text-cyan-200">{tactical.home_formation}</span>
            <span className="text-slate-500 mx-1">vs</span>
            <span className="font-mono text-cyan-200">{tactical.away_formation}</span>
            <span className="text-slate-500 mx-1">→</span>
            <span className="text-cyan-300">{tactical.clash_type?.replace(/_/g, ' ') || 'matchup'}</span>
            {(tactical.home_xg_delta || tactical.away_xg_delta) && (
              <span className="block text-[10px] text-slate-400 mt-0.5 font-mono">
                ΔxG H {signed(tactical.home_xg_delta, 2)}  A {signed(tactical.away_xg_delta, 2)}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Audit trail */}
      {expanded && audit.length > 0 && (
        <ul
          className="mt-1 space-y-1 text-[10px] font-mono text-amber-500/70 bg-black/50 rounded-md p-2 border border-amber-500/10 max-h-40 overflow-y-auto"
          data-testid="harden-audit-list"
        >
          {audit.map((line, i) => (
            <li key={i} className="leading-relaxed">
              <span className="text-amber-500/40 mr-1.5">›</span>{line}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default HardeningChip;
